/**
 * Payment Service for StudyStay Website
 * Handles Stripe payments via Firebase Cloud Functions
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

const functions = getFunctions();
const auth = getAuth();

function getCallableErrorMessage(error, fallback) {
  if (!error) return fallback;
  if (error?.details?.message) return error.details.message;
  if (typeof error?.message === 'string') return error.message;
  return fallback;
}

function resolveAbsoluteUrl(path, fallbackPath) {
  const resolvedPath = path || fallbackPath;
  if (!resolvedPath) return null;
  if (/^https?:\/\//i.test(resolvedPath)) {
    return resolvedPath;
  }
  return `${window.location.origin}${resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`}`;
}

export async function createSubscriptionCheckout(plan, launch = false, options = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const callable = httpsCallable(functions, 'createSubscriptionCheckout');
    const result = await callable({
      plan,
      launch,
      successUrl: resolveAbsoluteUrl(options.successPath, '/billing/success'),
      cancelUrl: resolveAbsoluteUrl(options.cancelPath, '/billing/cancel')
    });

    return result.data?.url;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij starten van abonnement');
    throw new Error(message);
  }
}

/**
 * Activate the free Listing-Only plan (private or agency) directly,
 * without going through Stripe Checkout.
 */
export async function activateFreeListing(plan) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  if (plan !== 'listing_only_private' && plan !== 'listing_only_agency') {
    throw new Error("plan must be 'listing_only_private' or 'listing_only_agency'");
  }

  try {
    const callable = httpsCallable(functions, 'activateFreeListing');
    const result = await callable({ plan });
    return result.data;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij activeren van Listing-Only plan');
    throw new Error(message);
  }
}

export async function createCustomerPortal(options = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const callable = httpsCallable(functions, 'createCustomerPortal');
    const result = await callable({
      returnUrl: resolveAbsoluteUrl(options.returnPath, '/account')
    });

    return result.data?.url;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij openen van portal');
    throw new Error(message);
  }
}

export async function createVoorschotCheckout(contractId) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }
  if (!contractId) {
    throw new Error('Contract is verplicht');
  }

  try {
    const callable = httpsCallable(functions, 'paymentsCreateVoorschotCheckout');
    const result = await callable({
      contractId,
      successUrl: `${window.location.origin}/contracts/success`,
      cancelUrl: `${window.location.origin}/contracts/cancel`
    });
    return result.data?.url;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij starten van voorschot betaling');
    throw new Error(message);
  }
}

export async function createRentSubscriptionCheckout(contractId) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }
  if (!contractId) {
    throw new Error('Contract is verplicht');
  }

  try {
    const callable = httpsCallable(functions, 'paymentsCreateRentSubscriptionCheckout');
    const result = await callable({
      contractId,
      successUrl: `${window.location.origin}/contracts/success`,
      cancelUrl: `${window.location.origin}/contracts/cancel`
    });
    return result.data?.url;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij activeren van huurbetalingen');
    throw new Error(message);
  }
}

export async function cancelRentSubscription(contractId) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }
  if (!contractId) {
    throw new Error('Contract is verplicht');
  }

  try {
    const callable = httpsCallable(functions, 'paymentsCancelRentSubscription');
    const result = await callable({ contractId });
    return result.data;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij stopzetten van automatische betalingen');
    throw new Error(message);
  }
}

/**
 * Create a rent payment using Stripe Connect
 * @param {number} amount - Amount in cents
 * @param {string} landlordStripeAccount - Landlord's Stripe Connect account ID
 * @param {string} contractId - Optional contract ID
 * @returns {Promise<Object>} Payment intent details with clientSecret
 */
export async function createRentPayment(amount, landlordStripeAccount, contractId = null) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const payload = {
      amount,
      landlordStripeAccount,
      userId: user.uid
    };

    if (contractId) {
      payload.contractId = contractId;
    }

    const callable = httpsCallable(functions, 'createRentPayment');
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('Error creating rent payment:', error);
    throw error;
  }
}

/**
 * Boost pricing options
 */
export const BOOST_OPTIONS = [
  { id: 3, duration: '3 dagen', price: 3, icon: '⚡', color: 'orange' },
  { id: 7, duration: '7 dagen', price: 7, icon: '⭐', color: 'blue' },
  { id: 20, duration: '20 dagen', price: 15, icon: '👑', color: 'purple' }
];

/**
 * Premium subscription plans
 */
export const SUBSCRIPTION_PLANS = [
  {
    id: 'listing_only_private',
    name: 'Listing-Only',
    price: 0,
    period: 'gratis',
    properties: 'Onbeperkt',
    features: ['Onbeperkt panden', 'CRM integratie', 'Contact via e-mail'],
    notIncluded: ['Geen bezichtigingen', 'Geen contracten', 'Geen in-app chat', 'Geen property management'],
    popular: false,
    isFree: true
  },
  {
    id: 'listing_only_agency',
    name: 'Listing-Only (Agency)',
    price: 0,
    period: 'gratis',
    properties: 'Onbeperkt',
    features: ['Onbeperkt panden', 'CRM integratie', 'Contact via e-mail'],
    notIncluded: ['Geen bezichtigingen', 'Geen contracten', 'Geen in-app chat', 'Geen property management'],
    popular: false,
    isFree: true
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    period: 'gratis',
    properties: '1 actief pand',
    features: ['1 actief pand'],
    notIncluded: ['Geen verified badge', 'Geen agency features'],
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 19,
    period: 'per maand',
    properties: 'Onbeperkt',
    features: ['Onbeperkt actieve panden', 'Verified badge'],
    notIncluded: ['Geen agency features'],
    popular: true
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 79,
    period: 'per maand',
    properties: 'Onbeperkt',
    features: ['Agency dashboard', 'Onbeperkt actieve panden'],
    notIncluded: [],
    popular: false
  }
];

export async function createBoostCheckout(listingId, durationDays) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const callable = httpsCallable(functions, 'createBoostCheckout');
    const result = await callable({
      listingId,
      durationDays,
      successUrl: `${window.location.origin}/billing/success`,
      cancelUrl: `${window.location.origin}/billing/cancel`
    });

    return result.data?.url;
  } catch (error) {
    const message = getCallableErrorMessage(error, 'Fout bij starten van boost');
    throw new Error(message);
  }
}

const paymentService = {
  createSubscriptionCheckout,
  createCustomerPortal,
  createBoostCheckout,
  createRentPayment,
  createVoorschotCheckout,
  createRentSubscriptionCheckout,
  cancelRentSubscription,
  BOOST_OPTIONS,
  SUBSCRIPTION_PLANS
};

export default paymentService;

